import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { rooms } from '../../data/rooms';

export default function RoomsAndSuites() {
  const carouselRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(1); // Start at the first real room
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkIsMobile();
    window.addEventListener("resize", checkIsMobile);
    return () => window.removeEventListener("resize", checkIsMobile);
  }, []);

  const GAP = 24; // must match gap-6
  const numRealRooms = rooms.length;
  const displayRooms = [rooms[numRealRooms - 1], ...rooms, rooms[0]]; // Duplicate last and first for infinite effect

  const go = (dir) => {
    if (isTransitioning) return;

    setIsTransitioning(true);
    let nextIndex;

    if (dir === "left") {
      nextIndex = currentIndex - 1;
    } else {
      nextIndex = currentIndex + 1;
    }
    setCurrentIndex(nextIndex);
  };

  useEffect(() => {
    if (isMobile) return;
    const carousel = carouselRef.current;
    if (!carousel) return;

    const handleTransitionEnd = () => {
      setIsTransitioning(false);

      if (currentIndex === 0) {
        // If we've moved to the duplicated last room, jump to the real last room
        carousel.style.transitionDuration = '0s';
        setCurrentIndex(numRealRooms);
      } else if (currentIndex === numRealRooms + 1) {
        // If we've moved to the duplicated first room, jump to the real first room
        carousel.style.transitionDuration = '0s';
        setCurrentIndex(1);
      }
    };

    carousel.addEventListener('transitionend', handleTransitionEnd);

    // Re-enable transition after a short delay if it was disabled
    if (carousel.style.transitionDuration === '0s') {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          carousel.style.transitionDuration = '500ms';
        });
      });
    }

    return () => {
      carousel.removeEventListener('transitionend', handleTransitionEnd);
    };
  }, [currentIndex, numRealRooms, isTransitioning, isMobile]);

  return (
    <section id="rooms-and-suites" className={`w-full bg-gray-50 text-dark-green font-['Cormorant_Garamond',serif] py-16 sm:py-24 ${isMobile ? 'is-mobile' : ''}`}>
      {/* Header */}
      <div className="mx-auto max-w-6xl px-4 sm:px-6 text-center mb-12">
        <p className="text-beige text-sm tracking-[0.3em] font-semibold uppercase" data-aos="fade-up">
          • Our Accommodations •
        </p>
        <h2 className="mt-4 text-4xl sm:text-5xl font-bold tracking-tight" data-aos="fade-up">
          Rooms & Suites
        </h2>
        <p className="mt-5 max-w-3xl mx-auto text-lg leading-relaxed text-gray-600" data-aos="fade-up">
          Discover comfort and elegance in every corner of our tastefully designed rooms and suites. Blending traditional charm with modern amenities, each space offers a cozy retreat for a restful and memorable stay.
        </p>
      </div>

      {/* Carousel with 20% peeks on both sides */}
      <div className="relative mx-auto mt-8 sm:mt-12 max-w-[1400px]" data-aos="fade-up">
        {/* Viewport with side padding = 20% of viewport width so neighbors peek */}
        <div className="overflow-hidden relative rooms-suites-viewport" style={{ paddingLeft: "20vw", paddingRight: "20vw" }}>
          {/* Track */}
          <div
            ref={carouselRef}
            className="flex gap-6 transition-transform duration-500 ease-out rooms-suites-track"
            style={{ transform: `translateX(calc(-${currentIndex} * (60vw + ${GAP}px)))` }}
          >
            {displayRooms.map((room, index) => (
              <div key={`${room.id}-${index}`} className={`shrink-0 rooms-suites-card ${(index === 0 || index === displayRooms.length - 1) ? 'clone-room' : ''}`} style={{ width: "60vw", maxWidth: 920 }}>
                <article className="w-full rounded-lg bg-white shadow-xl overflow-hidden group transition-transform duration-300" data-aos="fade-up">
                  {/* Image frame */}
                  <div className="relative w-full h-[280px] sm:h-[400px] md:h-[480px] lg:h-[560px] overflow-hidden">
                    <img src={room.img} alt={room.title} className="absolute inset-0 h-full w-full object-cover group-hover:scale-[1.05] transition-transform duration-500" />
                    {room.tag && (
                      <span className="absolute bottom-4 left-4 rounded-full bg-dark-green/80 px-4 py-2 text-xs font-medium text-white shadow-md">
                        {room.tag}
                      </span>
                    )}
                  </div>

                  {/* Text content */}
                  <div className="p-6 sm:p-8">
                    <h3 className="text-2xl font-semibold tracking-tight text-dark-green">{room.title}</h3>
                    <p className="text-sm text-gray-600 mt-2">{room.subtitle}</p>
                    <div className="mt-6">
                      <Link to={`/rooms/${room.id}`} className="inline-flex items-center px-6 py-3 text-sm font-semibold tracking-wider uppercase text-white bg-beige rounded-md hover:bg-dark-green transition-colors duration-300 shadow-md">
                        View Details
                      </Link>
                    </div>
                  </div>
                </article>
              </div>
            ))}
          </div>
        </div>

        {/* Global Navigation Arrows */}
        <button
          onClick={() => go("left")}
          className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-3 shadow-lg hover:bg-white hover:scale-[1.1] transition-transform duration-300 z-20 hidden md:block"
          aria-label="Previous room"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-6 w-6 text-dark-green">
            <path fillRule="evenodd" d="M15.78 4.97a.75.75 0 010 1.06L9.81 12l5.97 5.97a.75.75 0 01-1.06 1.06l-6.5-6.5a.75.75 0 010-1.06l6.5-6.5a.75.75 0 011.06 0z" clipRule="evenodd" />
          </svg>
        </button>
        <button
          onClick={() => go("right")}
          className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-3 shadow-lg hover:bg-white hover:scale-[1.1] transition-transform duration-300 z-20 hidden md:block"
          aria-label="Next room"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-6 w-6 text-dark-green">
            <path fillRule="evenodd" d="M8.22 19.03a.75.75 0 010-1.06L14.19 12 8.22 6.03a.75.75 0 111.06-1.06l6.5 6.5a.75.75 0 010 1.06l-6.5 6.5a.75.75 0 01-1.06 0z" clipRule="evenodd" />
          </svg>
        </button>
      </div>
    </section>
  );
}